import { Link } from 'react-router-dom';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { PublicFooter, SUPPORT_EMAIL } from '@/components/PublicFooter';

const Terms = () => (
  <div className="min-h-screen flex flex-col">
    <header className="max-w-6xl w-full mx-auto flex items-center justify-between px-4 sm:px-8 py-6">
      <Link to="/" className="flex items-center gap-3">
        <ZitaLogo size={44} />
        <div className="flex flex-col leading-none">
          <ZitaWordmark className="text-3xl" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-zita-mocha mt-0.5">
            Powered by <span className="remod-accent font-semibold">RemodWorks</span>
          </span>
        </div>
      </Link>
      <nav className="flex items-center gap-3">
        <Link to="/login" className="text-zita-mocha text-sm hover:text-zita-espresso">
          Sign in
        </Link>
        <Link to="/register" className="zita-btn-primary text-sm">
          Start 3-day trial
        </Link>
      </nav>
    </header>

    <main className="flex-1">
      <article className="max-w-3xl mx-auto px-4 sm:px-8 py-10 space-y-6 text-zita-espresso">
        <div className="text-xs uppercase tracking-[0.18em] text-zita-mocha">Draft — pending owner + legal review</div>
        <h1 className="font-serif text-4xl">Zita — Terms and Conditions</h1>
        <p className="text-sm text-zita-mocha">Last updated: February 2026 (DRAFT)</p>

        <Section title="1. Acceptance">
          By creating a Zita account you agree to these Terms. If you do not agree, do not use Zita.
        </Section>
        <Section title="2. What Zita is">
          Zita is a private, AI-assisted marketplace scanning tool. Zita searches public listings across
          supported sources and analyses them with Ari. Zita does not buy, sell, message, negotiate, or
          list items on your behalf. Zita is not a marketplace or a broker.
        </Section>
        <Section title="3. Accounts">
          You must be at least 18. You must provide accurate information. You are responsible for your
          account and password. Do not share your account.
        </Section>
        <Section title="4. Paid membership &amp; free trial">
          Zita requires a paid membership after your free trial. New users receive a
          <strong> 3-day free trial with no credit card required at signup</strong>. When your trial ends
          your account locks until you subscribe. Membership renews automatically at
          $29.99/month until cancelled. Cancel anytime in Settings → Subscription.
          You keep access through the end of the current billing period; no partial or prorated refunds.
        </Section>
        <Section title="5. Refunds">
          Charges are non-refundable except where required by law. Contact support at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zita-cocoa hover:underline">{SUPPORT_EMAIL}</a> to review edge cases.
        </Section>
        <Section title="6. Zita browser extension">
          The optional Zita Guided Scan Extension runs in your own browser and uses your own signed-in
          sessions on third-party sites (e.g., Facebook Marketplace, OfferUp, Nextdoor). Zita never sees
          or stores your passwords for those sites. You are responsible for complying with each site's
          terms of use.
        </Section>
        <Section title="7. Third-party sites">
          Listings, prices, seller information, and images belong to their respective sources. Zita
          links out; the transaction occurs on the source site. Zita does not guarantee availability,
          accuracy, or condition.
        </Section>
        <Section title="8. AI outputs">
          Ari's condition estimates, repair-risk assessments, opportunity scores, and profit ranges are
          AI-generated and NOT guaranteed. Always verify in person before purchase or resale.
        </Section>
        <Section title="9. Local Buyer Network">
          Business listings in the Local Buyer Network come from Google Places. Zita does not vet or
          endorse any business. You are responsible for verifying who you sell to.
        </Section>
        <Section title="10. Acceptable use">
          Do not use Zita to scrape at excessive rates, bypass security, impersonate others, or violate
          any site's terms. Do not use Zita for unlawful purposes.
        </Section>
        <Section title="11. Data retention &amp; deletion">
          You can delete your saved listings and buyer vault at any time. Account deletion removes your
          profile and associated data on request — email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zita-cocoa hover:underline">{SUPPORT_EMAIL}</a>.
        </Section>
        <Section title="12. Disclaimers &amp; liability">
          Zita is provided "as is" without warranties. To the maximum extent permitted by law, Zita is
          not liable for indirect, incidental, or consequential damages, including lost profits or
          transactions.
        </Section>
        <Section title="13. Changes">
          We may update these Terms. Material changes will be communicated by email or in-app.
        </Section>
        <Section title="14. Contact">
          For account, billing, subscription, technical, or general support, email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zita-cocoa hover:underline">{SUPPORT_EMAIL}</a>.
        </Section>
      </article>
    </main>

    <PublicFooter />
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-serif text-2xl mt-6 mb-2">{title}</h2>
    <p className="text-sm text-zita-mocha leading-relaxed">{children}</p>
  </section>
);

export default Terms;
